import { ColorPalette } from './ColorPalette';
import type { ModeInterface } from './modes/ModeInterface';
import { StaticColorMode } from './modes/StaticColorMode';
import { SystemColorMode } from './modes/SystemColorMode';
import { RotateColorMode } from './modes/RotateColorMode';
import { ScheduleColorMode } from './modes/ScheduleColorMode';
import { DailyScheduleEvent } from './modes/schedule-events/DailyScheduleEvent';
import { WeekdayScheduleEvent } from './modes/schedule-events/WeekdayScheduleEvent';
import { DateScheduleEvent } from './modes/schedule-events/DateScheduleEvent';
import type { ModeConfig, ScheduleEventConfig } from './types';
import type { ScheduleEventInterface } from './modes/schedule-events/ScheduleEventInterface';

const PALETTE_CLASS_PREFIX = 'cmfe-color-mode-';
const MODE_TYPE_PREFIX = 'cmfe-mode-';

/** Minimal palette for class-only switching — no color map needed. */
function pal( key: string ): ColorPalette {
	return new ColorPalette( key, {} );
}

function buildScheduleEvent( cfg: ScheduleEventConfig ): ScheduleEventInterface {
	const palette = pal( cfg.palette );
	const tz = cfg.timezone ?? null;
	switch ( cfg.anchor ) {
		case 'time_only':
			return new DailyScheduleEvent( cfg.time ?? '00:00', palette, tz );
		case 'weekday':
			return new WeekdayScheduleEvent( cfg.weekday ?? 0, cfg.time ?? '00:00', palette, tz );
		case 'date': {
			const dt = cfg.datetime ? new Date( cfg.datetime.replace( ' ', 'T' ) ) : new Date( 0 );
			return new DateScheduleEvent( dt, palette );
		}
	}
}

function buildMode( config: ModeConfig ): ModeInterface {
	switch ( config.type ) {
		case 'static':   return new StaticColorMode( pal( config.palette ) );
		case 'system':   return new SystemColorMode( pal( config.dark ), pal( config.light ) );
		case 'rotator':  return new RotateColorMode( config.palettes.map( pal ), config.interval_seconds );
		case 'schedule': return new ScheduleColorMode( config.events.map( buildScheduleEvent ) );
	}
}

/**
 * Resolves the active palette for a mode strategy and keeps the target
 * element's CSS classes in sync. Only switches classes — does not touch CSS.
 */
export class PaletteController {
	private readonly target: HTMLElement;
	private readonly mode: ModeInterface;

	constructor( mode: ModeInterface, target: HTMLElement ) {
		this.mode = mode;
		this.target = target;
		this.mode.setPaletteStaleListener( () => this.applyPalette() );
		this.applyPalette();
	}

	/** Build a controller directly from a PHP mode config object. */
	static createFromConfig( config: ModeConfig, target: HTMLElement ): PaletteController {
		return new PaletteController( buildMode( config ), target );
	}

	/**
	 * Reconstruct a ModeConfig from an element's data-cmfe-* attributes.
	 * Returns null when no type attribute is present.
	 */
	static readConfigFromElement( el: HTMLElement ): ModeConfig | null {
		const raw: Record<string, unknown> = {};
		for ( const attr of Array.from( el.attributes ) ) {
			if ( ! attr.name.startsWith( 'data-cmfe-' ) ) continue;
			const key = attr.name.slice( 'data-cmfe-'.length );
			try { raw[ key ] = JSON.parse( attr.value ); }
			catch { raw[ key ] = attr.value; }
		}
		return typeof raw[ 'type' ] === 'string' ? ( raw as unknown as ModeConfig ) : null;
	}

	getPalette(): ColorPalette {
		return this.mode.currentPalette();
	}

	setPaletteByKey( key: string ): void {
		this.setPalette( new ColorPalette( key, {} ) );
	}

	setPalette( palette: ColorPalette ): void {
		console.log( '[cmfe] setPalette', { key: palette.key(), target: this.target } );
		for ( const cls of Array.from( this.target.classList ) ) {
			if ( cls.startsWith( PALETTE_CLASS_PREFIX ) ) {
				this.target.classList.remove( cls );
			}
		}
		this.target.classList.add( PALETTE_CLASS_PREFIX + palette.key() );
	}

	applyPalette(): void {
		console.log( '[cmfe] applyPalette', { type: this.mode.type(), palette: this.getPalette().key(), target: this.target } );
		for ( const cls of Array.from( this.target.classList ) ) {
			if ( cls.startsWith( MODE_TYPE_PREFIX ) ) {
				this.target.classList.remove( cls );
			}
		}
		this.target.classList.add( MODE_TYPE_PREFIX + this.mode.type() );
		this.setPalette( this.getPalette() );
	}

	dispose(): void {
		this.mode.dispose();
	}
}
